<template>
    <Modal id="upgrade-needed" :show-footer="false" :show-title="false" design="v3" @cancel="cancel">
        <CreditCardNeeded @close="creditCardNeededModal = false" v-if="creditCardNeededModal" />
        <div class="upgrade-needed-content">
            <div class="upgrade-needed-content-title">
                <UpgradeIconSvg />
                <div>{{ $t("message.upgradePackageTitle") }}</div>
            </div>
            <div class="upgrade-needed-content-description">
                <template v-if="descriptions[upgradeDetail.name]">
                    {{ descriptions[upgradeDetail.name] }}
                </template>
                <template v-else>
                    {{ $t("message.moreFeaturesDesc") }}
                </template>
            </div>
            <div class="upgrade-needed-content-content">
                <UpgradeSVG />
            </div>
            <div v-if="nextPlan" class="upgrade-needed-content-suggestion">
                <div class="suggestion-columns" :class="{ single: !currentPlan }">
                    <div class="col current" v-if="currentPlan">
                        <div class="suggestion-badge">Mevcut Plan</div>
                        <div class="suggestion-title">{{ currentPlan.title }} {{ currentPlan.altTitle }}</div>
                        <div class="suggestion-metrics" v-if="currentSubscriptionDetailCount && detailUnitLabel">
                            <div class="metric">
                                <span class="metric-count">{{ currentSubscriptionDetailCount }}</span>
                                <span class="metric-unit">{{ detailUnitLabel }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="suggestion-arrow" v-if="currentPlan">
                        <i class="material-icons">arrow_forward</i>
                    </div>
                    <div class="col next">
                        <div class="suggestion-badge">Önerilen Plan</div>
                        <div class="suggestion-title">{{ nextPlan.title }} {{ nextPlan.altTitle }}</div>
                        <div
                            class="suggestion-metrics"
                            v-if="nextSubscription && nextSubscription.details && detailUnitLabel"
                        >
                            <div class="metric">
                                <span class="metric-count">{{ nextSubscriptionDetailCount }}</span>
                                <span class="metric-unit">{{ detailUnitLabel }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="upgrade-needed-content-footer">
                <template v-if="nextSubscription">
                    <SupsisButton
                        @click="upgrade"
                        primary
                        :disabled="isUpgrading"
                        :icon="isUpgrading ? 'autorenew' : ''"
                        :class-names="isUpgrading ? 'loading' : ''"
                    >
                        {{ isTrialMode ? "Ücretsiz Hemen Yükselt" : "Hemen Yükselt" }}
                    </SupsisButton>
                    <SupsisButton @click="goToPlans" secondary>
                        {{ isTrialMode ? "Ücretsiz Planları Görüntüle" : "Planları Görüntüle" }}
                    </SupsisButton>
                </template>
                <template v-else>
                    <SupsisButton @click="goToPlans" primary>
                        {{ isTrialMode ? "Ücretsiz Planları İncele" : "Planları İncele" }}
                    </SupsisButton>
                </template>
            </div>
        </div>
    </Modal>
</template>
<script>
import Modal from "../common/Modal2.vue";
import UpgradeSVG from "../svg/Upgrade.vue";
import UpgradeIconSvg from "./svg/UpgradeIconSvg.vue";
import SupsisButton from "../../views/Pages/Settings/components/SupsisButton.vue";
import { mapState, mapGetters, mapActions } from "vuex";
import { PLAN_DETAILS, PLAN_TYPES, CHANNEL_PLAN_TYPES } from "../../views/Pages/Billing/utils/util";
import _ from "lodash";
import CreditCardNeeded from "./CreditCardNeeded.vue";

export default {
    name: "UpgradeNeeded",
    props: {
        plan: {
            type: String,
            default: null,
        },
    },
    components: {
        SupsisButton,
        UpgradeIconSvg,
        Modal,
        UpgradeSVG,
        CreditCardNeeded,
    },
    computed: {
        ...mapGetters({
            isTrialMode: "site/isTrialMode",
            isSavedCreditCard: "site/isSavedCreditCard",
            isCreditCardCustomer: "site/isCreditCardCustomer",
        }),
        ...mapState({
            upgradeDetail: (state) => state.app.upgrade,
            currentPlans: (state) => state.site.currentPlans,
            allPlans: (state) => state.site.plans,
            usages: (state) =>
                (state.site.conversationStatistics.monthlyUsages || []).reduce(
                    (acc, usage) => {
                        acc.chatbotUsage += usage.chatbotUsage;
                        acc.automationTaskCounter += usage.automationTaskCounter;
                        acc.emailCount += usage.emailCount;
                        acc.uniqueEmailCount += usage.uniqueEmailCount;
                        acc.totalWebVisits += usage.totalWebVisits;
                        return acc;
                    },
                    {
                        chatbotUsage: 0,
                        automationTaskCounter: 0,
                        emailCount: 0,
                        uniqueEmailCount: 0,
                        totalWebVisits: 0,
                    }
                ),
        }),
        plans() {
            return this.allPlans.filter((plan) => plan.type === this.plan).sort((a, b) => a.order - b.order);
        },
        plansFeatures() {
            return _.reduce(
                this.plans,
                (result, plan) => {
                    result[plan._id] = plan.features.reduce((acc, featureItems) => {
                        const items = Object.keys(featureItems).filter((item) => item !== "type");
                        items.forEach((item) => {
                            acc[item] = featureItems[item];
                        });
                        return acc;
                    }, {});
                    return result;
                },
                {}
            );
        },
        totalUsage() {
            const usages = {
                chatbot: this.usages.chatbotUsage,
                automation: this.usages.automationTaskCounter,
                email: this.usages.emailCount,
                web: this.usages.totalWebVisits,
            };
            return usages[this.plan];
        },
        currentPlanAction() {
            return _.find(this.currentPlans, (plan) => plan.planType === this.plan);
        },
        currentPlan() {
            return _.find(this.plans, (plan) => plan._id === _.get(this.currentPlanAction, "planId", null));
        },
        nextPlan() {
            return _.get(this.nextSubscription, "plan", null);
        },
        isCountablePlan() {
            return !!PLAN_DETAILS[this.plan];
        },
        currentDetailKey() {
            return PLAN_DETAILS[this.plan];
        },
        currentSubscriptionDetailCount() {
            const key = this.currentDetailKey;
            const details = _.get(this.currentPlanAction, "details", {});
            const count = _.get(details, key, 0);
            return count || 0;
        },
        nextSubscriptionDetailCount() {
            const key = this.currentDetailKey;
            const details = _.get(this.nextSubscription, "details", {});
            const count = _.get(details, key, 0);
            return count || 0;
        },
        detailUnitLabel() {
            const type = this.plan;
            if (type === "supsis") {
                return this.$t("chatWindow.agent");
            } else if (type === "chatbot") {
                return this.$t("report.session");
            } else if (type === "automation") {
                return this.$t("task.task");
            } else if (type === "mail") {
                return this.$t("visitor.mail");
            } else if (CHANNEL_PLAN_TYPES.indexOf(type) !== -1 || type === "webphone") {
                return this.$t("message.channel");
            } else {
                return "";
            }
        },
        selectFeatures() {
            return _.get(this.currentPlan, "selectFeatures", []);
        },
        isFeatureRequired() {
            const detailFeature = [
                "maxAgentCount",
                "maxMailCount",
                "maxWhatsappChannel",
                "maxBusinessWhatsappChannel",
                "maxInstagramChannel",
                "maxMessengerChannel",
                "maxTelegramChannel",
                "maxTrendyolChannel",
                "maxMetaLeadChannel",
                "maxWebphoneChannel"
            ];
            return !detailFeature.includes(this.upgradeDetail.name);
        },
        nextSubscription() {
            const plans = this.plans.filter((plan) => !plan.isFreePlan && !plan.isTeamPlan && !plan.isEnterPrise);
            const isFeatureRequired = this.isFeatureRequired;
            const currentPeriod = _.get(this.currentPlanAction, "period", "monthly");
            let currentPlanDetails = _.get(this.currentPlanAction, "details", {});

            let newNextPlan = this.currentPlan;
            if (newNextPlan) {
                newNextPlan = newNextPlan.isFreePlan ? plans[0] : newNextPlan;
            }
            if (isFeatureRequired) {
                const currentPlanFeatures = this.plansFeatures[this.currentPlan?._id];
                const nextPlan = plans.find((plan) => {
                    const features = this.plansFeatures;
                    const featureKey = this.featureKeys[this.upgradeDetail.name] || this.upgradeDetail.name;
                    const feature = features[plan._id][featureKey] || 0;
                    const isNumberFeature = typeof feature === "number";
                    const isExistFeature = isNumberFeature
                        ? feature === -1 || feature > _.get(currentPlanFeatures, this.upgradeDetail.name, 0)
                        : !!feature;
                    const selectFeatures = _.get(plan, "selectFeatures", []);
                    const defaultSelectedFeatureCount = currentPlanDetails[this.currentDetailKey];
                    const isFeatureEqual =
                        !selectFeatures.length ||
                        selectFeatures.find((feature) => {
                            const count = _.get(feature, "count", 0);
                            return defaultSelectedFeatureCount
                                ? defaultSelectedFeatureCount === count || count >= defaultSelectedFeatureCount
                                : count >= this.totalUsage;
                        });
                    return isExistFeature && isFeatureEqual;
                });

                newNextPlan = nextPlan;
                const selectFeatures = _.get(nextPlan, "selectFeatures", []);
                const defaultSelectedFeatureCount = currentPlanDetails[this.currentDetailKey];
                const isFeatureEqual = selectFeatures.length > 0;
                if (isFeatureEqual) {
                    const selectedFeature = selectFeatures.find((feature) => {
                        const count = _.get(feature, "count", 0);
                        return defaultSelectedFeatureCount
                            ? defaultSelectedFeatureCount === count || count >= defaultSelectedFeatureCount
                            : count >= this.totalUsage;
                    });
                    if (selectedFeature) {
                        const selectedFeatureDetails = _.get(selectedFeature, "details", {});
                        currentPlanDetails = Object.assign({}, currentPlanDetails, {
                            [this.currentDetailKey]: _.get(selectedFeature, "count", 0),
                            ...selectedFeatureDetails,
                        });
                    }
                }
            } else {
                if (this.currentPlan) {
                    const currentPlanActionDetails = this.currentPlanAction.details;
                    const nextPlan = this.currentPlan.isFreePlan ? plans[0] : this.currentPlan;
                    const isSupsisPlan = this.plan === "supsis";
                    if (isSupsisPlan) {
                        const currentPlanFeatures = this.plansFeatures[this.currentPlan?._id];
                        const newPlanFeatures = this.plansFeatures[nextPlan._id];
                        const oldFreeAgent = currentPlanFeatures.freeAgentCount;
                        const newFreeAgent = newPlanFeatures.freeAgentCount;
                        if (!oldFreeAgent && newFreeAgent > 0) {
                            newNextPlan = nextPlan;
                        } else {
                            currentPlanDetails = Object.assign({}, currentPlanDetails, {
                                [this.currentDetailKey]: currentPlanActionDetails[this.currentDetailKey] + 1,
                            });
                        }
                    } else if(this.currentDetailKey) {
                        currentPlanDetails = Object.assign({}, currentPlanDetails, {
                            [this.currentDetailKey]: currentPlanActionDetails[this.currentDetailKey] + 1,
                        });
                    }
                } else {
                    newNextPlan = plans[0];
                    if (this.currentDetailKey) {
                        currentPlanDetails = Object.assign({}, currentPlanDetails, {
                            [this.currentDetailKey]: 1,
                        });
                    }
                }
            }

            return (
                newNextPlan && {
                    plan: newNextPlan,
                    period: currentPeriod,
                    details: currentPlanDetails,
                }
            );
        },
    },
    data: function () {
        return {
            creditCardNeededModal: false,
            isUpgrading: false,
            descriptions: {
                maxAgentCount: this.$t("upgradeMessages.maxAgentCount"),
                maxTriggerCount: this.$t("upgradeMessages.maxTriggerCount"),
                visitorStartChat: this.$t("upgradeMessages.visitorStartChat"),
                voiceCalling: this.$t("upgradeMessages.voiceCalling"),
                videoCalling: this.$t("upgradeMessages.videoCalling"),
                fileAndVoiceMessage: this.$t("upgradeMessages.fileAndVoiceMessage"),
                autoTranslation: this.$t("upgradeMessages.autoTranslation"),
                telegramEntegration: this.$t("upgradeMessages.telegramEntegration"),
                chatCopilot: this.$t("upgradeMessages.chatCopilot"),
                calenderEntegration: this.$t("upgradeMessages.calenderEntegration"),
                mailEntegration: this.$t("upgradeMessages.mailEntegration"),
                smsEntegration: this.$t("upgradeMessages.smsEntegration"),
                ticketSystem: this.$t("upgradeMessages.ticketSystem"),
                taskManagementEntegration: this.$t("upgradeMessages.taskManagementEntegration"),
                webhookEntegration: this.$t("upgradeMessages.webhookEntegration"),
                readyContents: this.$t("upgradeMessages.readyContents"),
                automationEntegration: this.$t("upgradeMessages.automationEntegration"),
                customerTablesEntegration: this.$t("upgradeMessages.customerTablesEntegration"),
                formEntegration: this.$t("upgradeMessages.formEntegration"),
                maxFormLimit: this.$t("upgradeMessages.maxFormLimit"),
                maxFormFieldLimit: this.$t("upgradeMessages.maxFormFieldLimit"),
                maxCustomerTableCount: this.$t("upgradeMessages.maxCustomerTableCount"),
                maxCustomerTableFieldCount: this.$t("upgradeMessages.maxCustomerTableFieldCount"),
                maxCustomerTableViewCount: this.$t("upgradeMessages.maxCustomerTableViewCount"),
                maxCustomerTableRecordCount: this.$t("upgradeMessages.maxCustomerTableRecordCount"),
                maxAutomationButton: this.$t("upgradeMessages.maxAutomationButton"),
                automationTimerBase: this.$t("upgradeMessages.automationTimerBase"),
                automationEventBase: this.$t("upgradeMessages.automationEventBase"),
                automationWebhookBase: this.$t("upgradeMessages.automationWebhookBase"),
                automationButtonBase: this.$t("upgradeMessages.automationButtonBase"),
                liveScore: this.$t("upgradeMessages.liveScore"),
                multipleChatWindow: this.$t("upgradeMessages.multipleChatWindow"),
                maxTaskWorkflowLimit: this.$t("upgradeMessages.maxTaskWorkflowLimit"),
                maxTaskColumnLimit: this.$t("upgradeMessages.maxTaskColumnLimit"),
                maxTaskRowLimit: this.$t("upgradeMessages.maxTaskRowLimit"),
                maxDepartmentLimit: this.$t("upgradeMessages.maxDepartmentLimit"),
                maxRoleLimit: this.$t("upgradeMessages.maxRoleLimit"),
                maxReadyContentsLimit: this.$t("upgradeMessages.maxReadyContentsLimit"),
                excelDownload: this.$t("upgradeMessages.excelDownload"),
                customerGroup: this.$t("upgradeMessages.customerGroup"),
                maxMailCount: this.$t("upgradeMessages.maxMailCount"),
                maxWhatsappChannel: this.$t("upgradeMessages.maxWhatsappChannel"),
                maxBusinessWhatsappChannel: this.$t("upgradeMessages.maxBusinessWhatsappChannel"),
                maxInstagramChannel: this.$t("upgradeMessages.maxInstagramChannel"),
                maxMessengerChannel: this.$t("upgradeMessages.maxMessengerChannel"),
                maxTelegramChannel: this.$t("upgradeMessages.maxTelegramChannel"),
                maxTrendyolChannel: this.$t("upgradeMessages.maxTrendyolChannel"),
                maxMetaLeadChannel: this.$t("upgradeMessages.maxMetaLeadChannel"),
            },
            featureKeys: {
                maxFormLimit: "formLimit",
                maxFormFieldLimit: "formFieldLimit",
                maxCustomerTableCount: "customerTableCount",
                maxCustomerTableFieldCount: "customerTableFieldCount",
                maxCustomerTableViewCount: "customerTableViewCount",
                maxCustomerTableRecordCount: "customerTableRecordCount",
                maxAutomationButton: "automationBlock",
                maxTaskWorkflowLimit: "taskWorkflowLimit",
                maxTaskColumnLimit: "taskColumnLimit",
                maxTaskRowLimit: "taskRowLimit",
                maxDepartmentLimit: "departmentLimit",
                maxRoleLimit: "roleLimit",
                maxReadyContentsLimit: "readyContentsLimit",
            },
        };
    },
    methods: {
        ...mapActions({
            subscribePlan: "site/subscribePlan",
        }),
        cancel() {
            this.$emit("close");
        },
        contact() {
            if (typeof window !== "undefined" && window.open) {
                window.open("https://supsis.com", "_blank");
            }
            this.cancel();
        },
        upgrade() {
            if (!this.isTrialMode && !this.isSavedCreditCard && this.isCreditCardCustomer) {
                this.creditCardNeededModal = true;
                return;
            }

            this.isUpgrading = true;
            this.subscribePlan({
                planId: _.get(this.nextSubscription, "plan._id", null),
                period: this.nextSubscription.period,
                details: this.nextSubscription.details,
            })
                .then((result) => {
                    if (result.status === "error") {
                        this.$notification.error({
                            message: "Kart Hatası",
                            description: result.message,
                        });
                        return;
                    }

                    this.$notification.success({
                        message: "Başarılı",
                        description: "Yeni aboneliğiniz başarıyla başlatıldı.",
                    });
                    this.cancel();
                })
                .catch((err) => {
                    console.error("upgrade.flow", { type: "error", err });
                    this.$notification.error({
                        message: "Hata",
                        description: "Yükseltme sırasında bir sorun oluştu. Lütfen tekrar deneyin.",
                    });
                })
                .finally(() => {
                    this.isUpgrading = false;
                });
        },
        goToPlans() {
            this.$router.push(`/billing/plans/${this.plan === "meta-leads" ? "meta-lead" : this.plan}`);
            this.cancel();
        },
    },
};
</script>
<style lang="scss">
#upgrade-needed {
    .ant-modal {
        width: max-content !important;
    }

    .ant-modal-content {
        box-shadow: none;
    }

    .ant-modal-mask {
        background-color: rgba(0, 0, 0, 0.6) !important;
    }

    .upgrade-needed-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        &-title {
            svg {
                width: 28px;
                height: 28px;
            }

            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 20px;
            font-weight: bold;
        }

        &-description {
            text-align: center;
            width: 600px;
            font-size: 16px;
        }

        &-suggestion {
            margin-top: 14px;
            padding: 12px 16px;
            width: 600px;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;

            .suggestion-columns {
                display: grid;
                grid-template-columns: 1fr auto 1.1fr;
                gap: 12px;
                width: 100%;
                align-items: stretch;
            }

            .suggestion-columns.single {
                grid-template-columns: 1fr;
                justify-items: center;
            }

            .suggestion-columns.single .col.next {
                max-width: 360px;
            }

            .col {
                background: #ffffff;
                border: 1px solid #22272d12;
                border-radius: 10px;
                padding: 12px;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: center;
            }

            .col.current {
                background: #fafbff;
                opacity: 0.9;
            }

            .col.next {
                background: linear-gradient(180deg, #f6f6ff 0%, #ffffff 60%);
                border-color: #46499d;
                box-shadow: 0 6px 16px rgba(70, 73, 157, 0.25);
            }

            .suggestion-arrow {
                display: flex;
                align-items: center;
                justify-content: center;
                color: #46499d;
                padding: 0 4px;

                i {
                    font-size: 20px;
                }
            }

            .suggestion-badge {
                background: #f1f1ff;
                color: #46499d;
                border: 1px solid #46499d;
                font-size: 11px;
                padding: 2px 8px;
                border-radius: 20px;
                letter-spacing: 0.2px;
                text-transform: uppercase;
                font-weight: 600;
            }

            .col.next .suggestion-badge {
                background: #46499d;
                color: #fff;
            }

            .suggestion-title {
                margin-top: 8px;
                font-weight: 600;
                font-size: 16px;
            }

            .col.next .suggestion-title {
                font-size: 17px;
            }

            .suggestion-desc {
                margin-top: 4px;
                font-size: 14px;
                color: var(--color-text-default);
            }

            .suggestion-metrics {
                display: flex;
                gap: 8px;
                margin-top: 8px;

                .metric {
                    display: inline-flex;
                    align-items: baseline;
                    gap: 4px;
                    background: white;
                    border: 1px solid #22272d12;
                    border-radius: 8px;
                    padding: 4px 8px;
                }

                .metric-count {
                    font-weight: 800;
                    color: #46499d;
                }

                .metric-unit {
                    color: #647491;
                    font-size: 12px;
                }
            }
        }

        &-content {
            width: 240px;
            margin-top: 16px;
            margin-bottom: 16px;

            svg {
                width: 100%;
                height: 100%;
            }
        }

        &-footer {
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin-top: 25px;

            .supsis-button {
                min-width: 280px;
            }

            .supsis-button.loading .material-icons {
                animation: spin 1s linear infinite;
            }
        }
    }
}

.ant-notification {
    z-index: 9999 !important;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
